import { Injectable } from '@angular/core';
import { Filters, FilterSet } from 'src/app/global/filter-model';
import { Country, ContractModelBody } from 'src/app/global/types';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { HierarchyResponse } from 'src/app/global/ebr-models';
import { HttpClientService } from './http-client.service';
import { UserService } from './user.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FilterRequests {
  controller = new AbortController();
  private ENDPOINT_BASE_PATH: string = '/filters';

  excludeDeletedAccountsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  excludeDeletedAccountsObservable = this.excludeDeletedAccountsSubject.asObservable();

  constructor(
    private httpClientService: HttpClientService,
    private userService: UserService
  ) {}

  setExcludeDeletedAccounts(value: boolean) {
    this.excludeDeletedAccountsSubject.next(value);
  }

  getExcludeDeletedAccounts(): Observable<boolean> {
    return this.excludeDeletedAccountsObservable;
  }

  async getCountryCodeAndNames(
    regions: string[],
    divDataView: string = ''
  ): Promise<Country[]> {
    let url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/GetCountries?useCache=false`;
    const payload = regions;

    const { data } = await this.httpClientService.post(url, payload);

    return data.filter((item: any) => item != null);
  }

  async getTopLevelHierarchyAndCustomerNames(
    regions: string[],
    countryCode: string[],
    divDataView: string = '',
    division: string = ''
  ) {
    const payload : ContractModelBody = {
      topLevelContractGroup: [],
      secondLevelContractFilter: [],
      thirdLevelContractFilter: [],
      fourthLevelContractFilter: [],
      accts: [],
      division,
      dataView: divDataView,
      excludeDeletedAccounts: this.excludeDeletedAccountsSubject.value
    };
    let url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/ctr-filters/H5?region=${regions}&countries=${countryCode}`;

    const { data } = await this.httpClientService.post(url, payload);

    return data.filter((item: any) => item != null);
  }

  async getH5ForH7Numbers(
    regions: string[],
    countryCode: string[],
    divDataView: string = '',
    h7Numbers: string[] = [],
    division: string = ''
  ): Promise<string[]> {
    const payload: ContractModelBody = {
      topLevelContractGroup: [],
      secondLevelContractFilter: h7Numbers.map(str => str.split(' ')[0]),
      thirdLevelContractFilter: [],
      fourthLevelContractFilter: [],
      accts: [],
      division,
      dataView: divDataView,
      excludeDeletedAccounts: this.excludeDeletedAccountsSubject.value
    };
    const url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/ctr-filters/H5?region=${regions}&countries=${countryCode}`;
    const { data } = await this.httpClientService.post(url, payload);
    return data.filter((item: any) => item != null);
  }

  async getH7AndCustomerNames(
    regions: string[],
    countryCode: string[],
    divDataView: string = '',
    h5Numbers: string[] = [],
    Division: string = ''
  ) {
    const payload: ContractModelBody = {
      topLevelContractGroup: h5Numbers.map((str) => str.split(' ')[0]),
      secondLevelContractFilter: [],
      thirdLevelContractFilter: [],
      fourthLevelContractFilter: [],
      accts: [],
      division: Division,
      dataView: divDataView,
      excludeDeletedAccounts: this.excludeDeletedAccountsSubject.value
    };

    const url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/ctr-filters/H7?region=${regions}&countries=${countryCode}`;

    const { data } = await this.httpClientService.post(url, payload);

    return data.filter((item: any) => item != null);
  }

  async getAccountNumbersAndNames(
    regions: string[],
    countryCode: string[],
    divDataView: string = '',
    h5Numbers: string[],
    h7Numbers: string[],
    division: string = ''
  ) {
    const payload: ContractModelBody = {
      topLevelContractGroup: h5Numbers.map(str => str.split(" ")[0]),
      secondLevelContractFilter: h7Numbers.map(str => str.split(" ")[0]),
      thirdLevelContractFilter: [],
      fourthLevelContractFilter: [],
      accts: [],
      division,
      dataView: divDataView,
      excludeDeletedAccounts: this.excludeDeletedAccountsSubject.value
    };

    const url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/ctr-filters/Acct?region=${regions}&countries=${countryCode}`;

    const { data } = await this.httpClientService.post(url, payload);

    return data.filter((item: any) => item != null);
  }

  //#region Cust View Filters
  async getTopLevelHierarchyCustomerNames(
    regions: string[],
    countryCode: string[],
    divDataView: string = '',
  ) {
    let url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/customers?region=${regions}&countries=${countryCode}`;

    const { data } = await this.httpClientService.get(url);

    return data.filter((item: any) => item != null);
  }

  async getSubBrands(
    regions: string[],
    countryCode: string[],
    customers: string[] = [],
    divDataView: string = '',
    division: string
  ) {
    let url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/sub-brands?region=${regions}&countries=${countryCode}`;

    const { data } =  await this.httpClientService.post(url, {
      Customers: customers,
      Division: division,
      DataView: divDataView,
    });

    return data.filter((item: any) => item != null);
  }
  //#endregion
  async getCustomerNumbersAndNames(
    regions: string[],
    countryCode: string[],
    customers: string[] = [],
    divDataView: string = '',
    subBrands: string[] = [],
    division: string
  ){
    let url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/customer-number-name?region=${regions}&countries=${countryCode}`;

    const { data } = await this.httpClientService.post(url, {
      Customers: customers,
      SubBrands: subBrands,
      Division: division,
      DataView: divDataView,
    });

    return data.filter((item: any) => item != null);
  }

  async getHierarchy(division: string, filterSet: FilterSet): Promise<HierarchyResponse> {
    const { data } = await this.httpClientService.post<HierarchyResponse>(
      `${this.ENDPOINT_BASE_PATH}/${division}/hierarchy`,
      filterSet
    );

    return data;
  }

  async getSavedFilterSets(divDataView: string = '') {
    const userName = this.userService.getUsername()!.toLowerCase();

    // TODO: Consider not sending the username as it is now available in the access token sent to the API
    return await this.httpClientService.get(
      `${this.ENDPOINT_BASE_PATH}/${divDataView}/user/${userName}/savedFilters`
    );
  }

  // TODO : phase out ctrActModel Saved filters
  async addSavedFilterSet(
    savedFilterSetName: string,
    filterRequest: Filters | undefined,
    divDataView: string = ''
  ) {
    const now = new Date();
    const yyyy = now.getFullYear();
    let month = now.getMonth() + 1;
    let day = now.getDay();
    let hour = now.getHours();
    let min = now.getMinutes();

    let dd = day < 10 ? '0' + day : day;
    let mm = month < 10 ? '0' + month : month;
    let hhh = hour;
    let mmm = min;

    const userName = this.userService.getUsername()!.toLowerCase()!;
    const sfRegion = localStorage.getItem('ebr' + divDataView + 'Region')!;
    const sfCountries = localStorage.getItem(
      'ebr' + divDataView + 'Countries'
    )!;
    const sfPeriod = localStorage.getItem('ebr' + divDataView + 'DateRange')!;
    const sfTemplates = localStorage.getItem(
      'ebr' + divDataView + 'Templates'
    )!;
    const sfLastUsed = yyyy + '-' + mm + '-' + dd + ' ' + hhh + ':' + mmm;

    const data = {
      h5LevelCtrGrp: filterRequest?.topLevelHierarchies?.map((t) => t.h5Number),
      h7LevelCtrGrp: filterRequest?.h7Agreements?.map((h) => h.number),
      accts: filterRequest?.customers?.map((c) => c.accountNumber),
    };

    // TODO: Consider not sending the username as it is now available in the access token sent to the API
    return await this.httpClientService.post(
      `${this.ENDPOINT_BASE_PATH}/${divDataView}/user/${userName}/savedFilters?filterName=${savedFilterSetName}&region=${sfRegion}&countries=${sfCountries}&period=${sfPeriod}&template=${sfTemplates}&lastUsed=${sfLastUsed}`,
      data
    );
  }

  async delSavedFilterSet(
    savedFilterSetName: string,
    divDataView: string = ''
  ) {
    const userName = this.userService.getUsername()!.toLowerCase();

    // TODO: Consider not sending the username as it is now available in the access token sent to the API
    return await this.httpClientService.delete(
      `${this.ENDPOINT_BASE_PATH}/${divDataView}/user/${userName}/savedFilters?filterName=${savedFilterSetName}`
    );
  }

  async addSavedFilterUser(divDataView: string = '') {
    const userName = this.userService.getUsername()!.toLowerCase();

    // TODO: Consider not sending the username as it is now available in the access token sent to the API
    return await this.httpClientService.post(
      `${this.ENDPOINT_BASE_PATH}/${divDataView}/user/${userName}`
    );
  }

  // Contract Model post request
  async getFilterOptionsByFilterType(
    division: string = '',
    filterType: string = '',
    topLevelContractGroup:string[] | undefined = undefined,
    secondLevelContractFilter:string[] | undefined = undefined,
    thirdLevelContractFilter:string[] | undefined = undefined,
    fourthLevelContractFilter:string[] | undefined =undefined,
    regions: string[] = [],
    countryCode: string[] =[]
  ) {

    const payload = {
      Division : division,
      DataView : "",
      TopLevelContractGroup: division == "Distributor" ? topLevelContractGroup ?? [] : topLevelContractGroup?.map(str => str.split(" ")[0]) ?? [],
      SecondLevelContractFilter: division == "Distributor" ? secondLevelContractFilter ?? [] : secondLevelContractFilter?.map(str => str.split(" ")[0]) ?? [],
      ThirdLevelContractFilter: thirdLevelContractFilter ?? [],
      FourthLevelContractFilter: fourthLevelContractFilter?.map(str => str.split(" ")[0]) ?? [],
      Accts: [],
    };

    const url = `${this.ENDPOINT_BASE_PATH}/${division}/ctr-filters/${filterType}?region=${regions}&countries=${countryCode}`;
    const { data } = await this.httpClientService.post<any, any, any>(
      url,
      payload
    );

    return data.filter((item: any) => item != null);
  }

  // contract model saved filter
  async addSavedFilterSetContractModel(
    savedFilterSetName: string,
    filterRequest: ContractModel | undefined,
    divDataView: string = '',
    customDateRange:any,
  ) {
    const now = new Date();
    const yyyy = now.getFullYear();
    let month = now.getMonth() + 1;
    let date = now.getDate();
    let hour = now.getHours();
    let min = now.getMinutes();
    let dd = date < 10 ? '0' + date : date;
    let mm = month < 10 ? '0' + month : month;
    let hhh = hour;
    let mmm = min;
    const userName = this.userService.getUsername()!.toLowerCase()!;
    const sfRegion = localStorage.getItem('ebr' + divDataView + 'Region')!;
    const sfCountries = localStorage.getItem(
      'ebr' + divDataView + 'Countries'
    )!;
    const sfPeriod = localStorage.getItem('ebr' + divDataView + 'DateRange')!;
    const sfTemplates = localStorage.getItem(
      'ebr' + divDataView + 'Templates'
    )!;
    const sfLastUsed = yyyy + '-' + mm + '-' + dd + ' ' + hhh + ':' + mmm;

    // TODO: Consider not sending the username as it is now available in the access token sent to the API
    let url = `${this.ENDPOINT_BASE_PATH}/${divDataView}/user/${userName}/savedFilters-v1?filterName=${savedFilterSetName}&region=${sfRegion}&countries=${sfCountries}&period=${sfPeriod}&template=${sfTemplates}&lastUsed=${sfLastUsed}`;
    url = this.appendCustomDateRangeToUrl(url, sfPeriod, customDateRange);

    return await this.httpClientService.post(
      url,
      filterRequest
    );

  }

  private appendCustomDateRangeToUrl(url: string, periodSelected:string | undefined, customDateRange:string): string {
    if (periodSelected === 'Custom') {
      return `${url}&dateRange=${customDateRange}`;
    }
    return url;
  } 
}



